// @flow
import type {
  ComposeDataTableOptions,
  DataTableModule,
  DataTableState,
  ActionCreator,
  DataSelector,
  MakeRecordsSelector,
} from '../../types'

// Composers
import composeActionTypes from './composeActionTypes'
import composeActions from './composeActions'
import composeInitialState from './composeInitialState'
import composeSelectors from './composeSelectors'
import composeReducers from './composeReducers'
import composeContainer from './composeContainer'
import composeClass from './composeClass'

// Components

// M = Model e.g. { id: 4, parent: 10, date: 1432554 } (raw model)
// R = Record e.g. { id: 4, parent: 'Chinook', date: 'Monday', other: 'Hello World' } (sortable, filterable record)

export default function composeDataTable<M: { id: number }, R: { id: number }> ({
  name,
  options: maybeOptions = {},
  }: {
  name: string,
  options?: Object,
}): ({
  data$: DataSelector<M>,
  tableState$: (state: Object) => DataTableState,
  makeRecords: MakeRecordsSelector<M, R>
}) => DataTableModule<M, R> {
  return function ({
    data$,
    tableState$,
    makeRecords,
    }: {
    data$: DataSelector<M>,
    tableState$: (state: Object) => DataTableState,
    makeRecords: MakeRecordsSelector<M, R>
  }): DataTableModule<M, R> {
    //
    const options: ComposeDataTableOptions = {
      allowGrouping: false,
      allowColumnOrdering: false,
      allowSelecting: true,
      allowSorting: true,
      allowFiltering: true,
      allowPagination: true,
      allowSelectAll: true,
      ...(maybeOptions || {}),
    }

    const actionTypes = composeActionTypes({name, options})
    const actionCreators: {[string]: ActionCreator<any> } = composeActions({name, options, actionTypes})
    const initialState: DataTableState = composeInitialState()
    const selectors = composeSelectors({options, data$, tableState$, makeRecords})
    const reducers = composeReducers({options, actionTypes})
    const container = composeContainer({options, selectors, actionCreators})
    const DataTable = composeClass({options, container})

    return {
      initialState,
      actionCreators,
      selectors,
      reducers,
      DataTable,
    }
  }
}
